import { roundUpToNearestZloty } from '@algoacademy/backend/modules/account/mentoring-prices'
import {
  PAYMENT__BILLING_CADENCE,
  PAYMENT__DISCOUNT_SHAPE,
  type PaymentBillingCadence,
  type PaymentDiscountShape
} from '@algoacademy/backend/modules/payment/enums'

/** Stripe charges in the smallest currency unit, so every figure here is grosze. */
const GROSZE_PER_ZLOTY = 100

/** One billing period in ms — aligned with subscription renewal cadence. */
const BILLING_PERIOD_MS: Record<PaymentBillingCadence, number> = {
  [PAYMENT__BILLING_CADENCE.PER_4_WEEKS]: 28 * 24 * 60 * 60 * 1000,
  [PAYMENT__BILLING_CADENCE.PER_12_WEEKS]: 84 * 24 * 60 * 60 * 1000,
  [PAYMENT__BILLING_CADENCE.YEARLY]: 365 * 24 * 60 * 60 * 1000
}

/** What the offer stores about a staff discount - a shape and a length, never a price. */
export type MentoringDiscount = {
  shape: PaymentDiscountShape
  /** Percent off the invoice (1 to 90), or grosze off one billing period (invoice). */
  value: number
  /** Billing periods (invoices) the discount covers; null means it never ends. */
  periodCount: number | null
}

/** One Stripe subscription phase: an amount, and how many invoices carry it. */
export type DiscountPhase = {
  amountInGrosze: number
  iterations: number
}

/** No invoice ever leaves checkout below 20 zł gross, discounted or not. */
export const MINIMUM_INVOICE_GROSZE = 20 * GROSZE_PER_ZLOTY

/** Grosze in, grosze out, rounded up the way every other price on the site is. */
function roundUpGroszeToWholeZloty(grosze: number): number {
  return roundUpToNearestZloty(grosze / GROSZE_PER_ZLOTY) * GROSZE_PER_ZLOTY
}

/**
 * Turns one list invoice into what the buyer is actually charged while the staff
 * discount applies to that billing period.
 */
export function calculateDiscountedInvoiceGrosze(args: {
  listInvoiceGrosze: number
  discount: MentoringDiscount
}): number {
  const { listInvoiceGrosze, discount } = args

  const discountGrosze =
    discount.shape === PAYMENT__DISCOUNT_SHAPE.PERCENT
      ? (listInvoiceGrosze * discount.value) / 100
      : discount.value

  const chargedGrosze = roundUpGroszeToWholeZloty(listInvoiceGrosze - discountGrosze)

  // A discount that digs too deep is cut back rather than refused - staff keeps
  // the offer, the buyer just stops at the floor.
  return Math.max(chargedGrosze, MINIMUM_INVOICE_GROSZE)
}

/**
 * Walks the invoices the subscription issues while the discount still has
 * billing periods left and groups the identical ones, which is exactly the shape
 * a Stripe subscription schedule wants. The full-price phase that follows is
 * the caller's to add - here it is only the frozen figure to return to.
 */
export function planDiscountPhases(args: {
  listInvoiceGrosze: number
  discount: MentoringDiscount
}): { phases: DiscountPhase[]; fullPriceGrosze: number } {
  const { listInvoiceGrosze, discount } = args
  const fullPriceGrosze = listInvoiceGrosze

  // A discount that never ends has nothing to schedule: the subscription simply
  // renews cheaper until someone changes it.
  if (discount.periodCount === null) {
    return { phases: [], fullPriceGrosze }
  }

  const amountInGrosze = calculateDiscountedInvoiceGrosze({ listInvoiceGrosze, discount })

  return {
    phases: [{ amountInGrosze, iterations: discount.periodCount }],
    fullPriceGrosze
  }
}

/**
 * How many billing periods of the discount are still ahead, for the field staff
 * retypes when changing a discounted subscription. Only whole periods are spent:
 * a period half gone is still a period the student gets cheaper.
 */
export function countRemainingDiscountPeriods(args: {
  periodCount: number | null
  startedAt: Date
  now: Date
  billingCadence: PaymentBillingCadence
}): number | null {
  const { periodCount, startedAt, now, billingCadence } = args

  if (periodCount === null) return null

  const elapsedPeriods = Math.max(
    Math.floor((now.getTime() - startedAt.getTime()) / BILLING_PERIOD_MS[billingCadence]),
    0
  )

  return Math.max(periodCount - elapsedPeriods, 0)
}
